import { BehaviorSubject, from, interval, Observable, of, ReplaySubject } from 'rxjs';
import { first, map, share, startWith, switchMap } from 'rxjs/operators';

import { validateResponse } from './http.js';
import { buildPrompt } from './prompts.js';

export let geoLocationEnabled$ = new BehaviorSubject(false);

let geoNames$ = of(undefined).pipe(
    switchMap(() => from(fetch('/geonames.csv'))),
    validateResponse(),
    switchMap(res => from(res.text())),
    map(csv => {
        let geonames = [];
        for (let line of csv.split('\n')) {
            line = line.trim();
            if (line.length === 0) {
                continue;
            }
            let [name, lat, lng, size, nearIdx] = line.split('\t');
            geonames.push([
                name,
                parseFloat(lat),
                parseFloat(lng),
                parseInt(size),
                (nearIdx === '-') ? null : parseInt(nearIdx),
            ]);
        }
        return geonames;
    }),
    share({
        connector: () => new ReplaySubject(1),
        resetOnComplete: false,
        resetOnRefCountZero: false,
        resetOnError: true,
    }),
);

export let currentLocation$ = geoLocationEnabled$.pipe(
    switchMap(enabled => {
        if (!enabled) {
            return of(null);
        }
        return interval(5*60*1000).pipe(
            startWith(undefined),
            switchMap(() => new Observable(observer => {
                navigator.geolocation.getCurrentPosition(
                    pos => {
                        observer.next(pos);
                        observer.complete();
                    },
                    err => {
                        switch (err.code) {
                        default:
                            // at the time of writing this can never
                            // happend. but make errors crash that are
                            // added in the future.
                            observer.error(new Error(err.message));
                            break;
                        case GeolocationPositionError.PERMISSION_DENIED:
                        case GeolocationPositionError.POSITION_UNAVAILABLE:
                        case GeolocationPositionError.TIMEOUT:
                            // ollama-ui can life without a geo location
                            console.warn('Obtaining geo location failed:', err);
                            observer.complete();
                            break;
                        }
                    },
                    {
                        enableHighAccuracy: false,
                        timeout: 30*1000,
                        maximumAge: 4*60*1000,
                    });
            })),
            switchMap(pos => getLocationName(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy).pipe(
                switchMap(locations => {
                    if (locations.length === 1) {
                        return of({
                            location: locations[0],
                            accuracy: pos.coords.accuracy,
                        });
                    } else if (locations.length === 2) {
                        return buildPrompt('near', {
                            place: locations[0],
                            near: locations[1],
                        }).pipe(
                            map(location => ({
                                location,
                                accuracy: pos.coords.accuracy,
                            })),
                        );
                    } else {
                        throw new Error(`Unsupported locations length ${locations.length}`);
                    }
                }),
            )),
            // start with null so that currentLocation$ always emits a
            // value immediately.
            startWith(null),
        );
    }),
    share({
        connector: () => new ReplaySubject(1),
        resetOnComplete: true,
        resetOnRefCountZero: true,
        resetOnError: true,
    }),
);

function getLocationName(lat, lng, accuracy) {
    return geoNames$.pipe(
        first(),
        map(geoNames => {
            let minDist = null;
            let minLoc = null;
            for (let loc of geoNames) {
                let d = Math.max(0, dist(lat, lng, loc[1], loc[2]) - accuracy);
                if (minDist === null || d < minDist) {
                    minDist = d;
                    minLoc = loc;
                } else if (d == minDist && loc[3] > minLoc[3]) {
                    minLoc = loc;
                }
            }
            let nearLocIdx = minLoc[4];
            if (nearLocIdx === null) {
                return [minLoc[0]];
            } else {
                return [
                    minLoc[0],
                    geoNames[nearLocIdx][0],
                ];
            }
        }),
    );
}

function dist(lat1, lng1, lat2, lng2) {
    lat1 = lat1 / 180 * Math.PI;
    lng1 = lng1 / 180 * Math.PI;
    lat2 = lat2 / 180 * Math.PI;
    lng2 = lng2 / 180 * Math.PI;
    let dlng = lng2 - lng1;
    let dlat = lat2 - lat1;
    let x = Math.sin(dlat / 2);
    let y = Math.sin(dlng / 2);
    let a = x*x + Math.cos(lat1) * Math.cos(lat2) * y*y;
    let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return 6371 * c * 1000;
}

export let geonames$ = currentLocation$;
